import type {
  ApiEnvelope,
  Asset,
  Character,
  ComicJob,
  ComicPage,
  EditorState,
  Panel,
  ProjectData,
  ProductionRun,
  StoryboardDraft,
  StoryboardResult,
  StudioBootstrap,
} from "./types";

export class ComicApiError extends Error {
  readonly code: string;
  readonly currentRevision: number | null;
  readonly productionRunId: string;

  constructor(message: string, code = "request_failed", currentRevision: number | null = null, productionRunId = "") {
    super(message);
    this.name = "ComicApiError";
    this.code = code;
    this.currentRevision = currentRevision;
    this.productionRunId = productionRunId;
  }
}

function csrfToken(): string {
  const field = document.querySelector<HTMLInputElement>("[name=csrfmiddlewaretoken]");
  if (field?.value) {
    return field.value;
  }
  const match = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

async function request<T>(url: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) {
    headers.set("Content-Type", "application/json");
  }
  const token = csrfToken();
  if (token) {
    headers.set("X-CSRFToken", token);
  }
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: "same-origin",
  });
  let body: ApiEnvelope<T>;
  try {
    body = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ComicApiError(`サーバーから正しい応答を受け取れませんでした（HTTP ${response.status}）。`);
  }
  if (!response.ok || !body.success) {
    if (body.production_run_id && typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new CustomEvent("comic:production-open-requested", { detail: { runId: body.production_run_id } }));
    }
    const statusCode = {
      401: "authentication_required",
      403: "permission_denied",
      404: "not_found",
    }[response.status];
    throw new ComicApiError(
      body.error || `リクエストに失敗しました（HTTP ${response.status}）。`,
      body.error_code || statusCode || "request_failed",
      body.current_revision ?? null,
      body.production_run_id ?? "",
    );
  }
  return body;
}

const uncertainSubmissions = new Map<string, string>();

function productionUrl(studio: StudioBootstrap, action: "quote" | "list" | "detail" | "start" | "stop" | "resume", runId = ""): string {
  const projectId = studio.active_project?.id;
  if (!projectId) throw new ComicApiError("作品を開いてください。");
  const paths = {
    quote: studio.urls.production_quote_template,
    list: studio.urls.production_list_template,
    detail: studio.urls.production_detail_template,
    start: studio.urls.production_start_template,
    stop: studio.urls.production_stop_template,
    resume: studio.urls.production_resume_template,
  };
  const template = paths[action];
  if (!template) throw new ComicApiError("制作の受付先を確認できません。画面を再読み込みしてください。");
  return expand(expand(template, "__project_id__", projectId), "__run_id__", runId);
}

export async function listProductions(studio: StudioBootstrap): Promise<ProductionRun[]> {
  const result = await request<ProductionRun[]>(productionUrl(studio, "list"));
  return result.data ?? [];
}

export async function loadProduction(studio: StudioBootstrap, runId: string): Promise<ProductionRun> {
  const result = await request<ProductionRun>(productionUrl(studio, "detail", runId));
  if (!result.data) throw new ComicApiError("制作状況を取得できませんでした。");
  return result.data;
}

export async function quoteProduction(studio: StudioBootstrap, input: Record<string, unknown>): Promise<ProductionRun> {
  const result = await request<ProductionRun>(productionUrl(studio, "quote"), { method: "POST", body: JSON.stringify(input) });
  if (!result.data) throw new ComicApiError("制作内容を確認できませんでした。");
  return result.data;
}

export async function changeProduction(studio: StudioBootstrap, run: ProductionRun, action: "start" | "stop" | "resume", input: Record<string, unknown> = {}): Promise<ProductionRun> {
  try {
    const result = await request<ProductionRun>(productionUrl(studio, action, run.id), {
      method: "POST", body: JSON.stringify({ ...input, expected_revision: run.revision }),
    });
    if (!result.data) throw new ComicApiError("制作状況を確認できませんでした。追加の制作を始めず、状況を確認してください。");
    return result.data;
  } catch (error) {
    if (error instanceof ComicApiError && error.code === "revision_conflict") return loadProduction(studio, run.id);
    throw error;
  }
}

export async function loadStoryboardDraft(studio: StudioBootstrap, sourceJobId: string): Promise<StoryboardDraft> {
  const template = studio.urls.storyboard_draft_template;
  if (!template || !studio.active_project) throw new ComicApiError("下書きの取得先がありません。");
  const url = new URL(expand(template, "__project_id__", studio.active_project.id), window.location.origin);
  url.searchParams.set("source_job_id", sourceJobId);
  const result = await request<StoryboardDraft>(url.toString());
  if (!result.data) throw new ComicApiError("下書きを取得できませんでした。");
  return result.data;
}

export async function saveStoryboardDraft(studio: StudioBootstrap, draft: StoryboardDraft, content: StoryboardResult): Promise<StoryboardDraft> {
  const template = studio.urls.storyboard_draft_template;
  if (!template || !studio.active_project) throw new ComicApiError("下書きの保存先がありません。");
  const result = await request<StoryboardDraft>(expand(template, "__project_id__", studio.active_project.id), {
    method: "PATCH", body: JSON.stringify({ source_job_id: draft.source_job_id, expected_revision: draft.revision, content }),
  });
  if (!result.data) throw new ComicApiError("下書きを保存できませんでした。変更を保持しています。");
  return result.data;
}

async function submitJob(url: string, input: Record<string, unknown>): Promise<string> {
  const key = `comic-pending:${url}:${String(input.operation ?? "job")}`;
  let existing = uncertainSubmissions.get(key);
  try { existing = window.sessionStorage.getItem(key) ?? existing; } catch { /* Memory fallback for restricted storage. */ }
  const body = existing ?? JSON.stringify({...input, client_request_id:crypto.randomUUID()});
  uncertainSubmissions.set(key, body);
  try { window.sessionStorage.setItem(key, body); } catch { /* Keep the in-memory request. */ }
  const clear = () => {
    uncertainSubmissions.delete(key);
    try { window.sessionStorage.removeItem(key); } catch { /* Memory already cleared. */ }
  };
  if (existing && typeof window.dispatchEvent === "function") window.dispatchEvent(new CustomEvent("comic:submission-recovered"));
  try {
    const result = await request<never>(url, {method:"POST", body});
    if (!result.job_id) throw new ComicApiError("受付結果を確認できませんでした。同じ操作をもう一度押すと、追加課金せず前回の受付を確認します。");
    clear();
    return result.job_id;
  } catch (error) {
    if (error instanceof ComicApiError && !["request_failed", "service_unavailable"].includes(error.code)) clear();
    throw error;
  }
}

function expand(template: string, placeholder: string, value: string): string {
  return template.replace(placeholder, encodeURIComponent(value));
}

export async function loadBootstrap(url: string, projectId?: string): Promise<StudioBootstrap> {
  const target = new URL(url, window.location.origin);
  if (projectId) {
    target.searchParams.set("project_id", projectId);
  }
  const body = await request<StudioBootstrap>(target.toString());
  if (!body.studio) {
    throw new ComicApiError("サーバーから漫画ワークスペースのデータが返されませんでした。");
  }
  return body.studio;
}

export async function loadComicPage(studio: StudioBootstrap, pageId: string): Promise<ComicPage> {
  const url = expand(studio.urls.page_detail_template, "__page_id__", pageId);
  const body = await request<ComicPage>(url);
  if (!body.page) {
    throw new ComicApiError("ページ取得結果に漫画ページのデータがありません。");
  }
  return body.page;
}

export async function saveEditorState(
  studio: StudioBootstrap,
  page: ComicPage,
  editorState: EditorState,
): Promise<{ revision: number; savedAt: string }> {
  const url = expand(studio.urls.page_save_template, "__page_id__", page.id);
  const body = await request<never>(url, {
    method: "PATCH",
    body: JSON.stringify({
      expected_revision: page.revision,
      editor_state: editorState,
    }),
  });
  if (typeof body.revision !== "number" || !body.saved_at) {
    throw new ComicApiError("保存結果に新しいページバージョンがありません。");
  }
  return { revision: body.revision, savedAt: body.saved_at };
}

export async function savePanel(
  studio: StudioBootstrap,
  panel: Panel,
): Promise<Panel> {
  const url = expand(studio.urls.panel_update_template, "__panel_id__", panel.id);
  const body = await request<Panel>(url, {
    method: "PATCH",
    body: JSON.stringify({ content: panel.content }),
  });
  if (!body.panel) {
    throw new ComicApiError("コマの保存結果にコマデータがありません。");
  }
  return body.panel;
}

export async function loadPanelCandidates(
  studio: StudioBootstrap,
  panelId: string,
  offset = 0,
  limit = 20,
): Promise<{ assets: Asset[]; nextOffset: number; hasMore: boolean }> {
  const projectId = studio.active_project?.id;
  if (!projectId) {
    throw new ComicApiError("候補画像を読み込める漫画プロジェクトがありません。");
  }
  const template = expand(studio.urls.asset_list_template, "__project_id__", projectId);
  const url = new URL(template, window.location.origin);
  url.searchParams.set("panel_id", panelId);
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));
  const body = await request<Asset>(url.toString());
  return {
    assets: body.assets ?? [],
    nextOffset: body.next_offset ?? offset,
    hasMore: body.has_more ?? false,
  };
}

export async function loadAssets(
  studio: StudioBootstrap,
  filters: Record<string, string> = {},
  offset = 0,
  limit = 30,
): Promise<{ assets: Asset[]; nextOffset: number; hasMore: boolean }> {
  const projectId = studio.active_project?.id;
  if (!projectId) {
    throw new ComicApiError("素材を読み込める漫画プロジェクトがありません。");
  }
  const template = expand(studio.urls.asset_list_template, "__project_id__", projectId);
  const url = new URL(template, window.location.origin);
  for (const [key, value] of Object.entries(filters)) {
    if (value) {
      url.searchParams.set(key, value);
    }
  }
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));
  const body = await request<Asset>(url.toString());
  return {
    assets: body.assets ?? [],
    nextOffset: body.next_offset ?? offset,
    hasMore: body.has_more ?? false,
  };
}

export async function updateAssetFavorite(
  studio: StudioBootstrap,
  assetId: string,
  isFavorite: boolean,
): Promise<Asset> {
  const url = expand(studio.urls.asset_detail_template, "__asset_id__", assetId);
  const body = await request<Asset>(url, {
    method: "PATCH",
    body: JSON.stringify({ is_favorite: isFavorite }),
  });
  if (!body.asset) {
    throw new ComicApiError("素材の更新結果がありません。");
  }
  return body.asset;
}

export async function deleteAsset(studio: StudioBootstrap, assetId: string): Promise<void> {
  const url = expand(studio.urls.asset_detail_template, "__asset_id__", assetId);
  await request<never>(url, { method: "DELETE" });
}

export async function createPanel(
  studio: StudioBootstrap,
  pageId: string,
): Promise<Panel> {
  const url = expand(studio.urls.panel_list_template, "__page_id__", pageId);
  const body = await request<Panel>(url, { method: "POST" });
  if (!body.panel) {
    throw new ComicApiError("コマの作成結果にコマデータがありません。");
  }
  return body.panel;
}

export async function deletePanel(
  studio: StudioBootstrap,
  panelId: string,
): Promise<void> {
  const url = expand(studio.urls.panel_update_template, "__panel_id__", panelId);
  await request<never>(url, { method: "DELETE" });
}

export async function createCharacter(
  studio: StudioBootstrap,
  name: string,
  profile: Character["profile"],
): Promise<Character> {
  const projectId = studio.active_project?.id;
  if (!projectId) {
    throw new ComicApiError("キャラクターを追加できる漫画プロジェクトがありません。");
  }
  const url = expand(studio.urls.character_list_template, "__project_id__", projectId);
  const body = await request<Character>(url, {
    method: "POST",
    body: JSON.stringify({
      name,
      profile,
    }),
  });
  const character = (body as ApiEnvelope<Character> & { character?: Character }).character;
  if (!character) {
    throw new ComicApiError("キャラクターの保存結果にデータがありません。");
  }
  return character;
}

export async function updateCharacter(
  studio: StudioBootstrap,
  character: Character,
): Promise<Character> {
  const url = expand(
    studio.urls.character_detail_template,
    "__character_id__",
    character.id,
  );
  const body = await request<Character>(url, {
    method: "PATCH",
    body: JSON.stringify({ name: character.name, profile: character.profile }),
  });
  const saved = (body as ApiEnvelope<Character> & { character?: Character }).character;
  if (!saved) {
    throw new ComicApiError("キャラクターの保存結果にデータがありません。");
  }
  return saved;
}

export async function archiveCharacter(
  studio: StudioBootstrap,
  characterId: string,
): Promise<void> {
  const url = expand(
    studio.urls.character_archive_template,
    "__character_id__",
    characterId,
  );
  await request<never>(url, { method: "POST", body: "{}" });
}

export async function deleteScene(
  studio: StudioBootstrap,
  sceneId: string,
): Promise<void> {
  const projectId = studio.active_project?.id;
  if (!projectId) {
    throw new ComicApiError("シーンを削除できる漫画プロジェクトがありません。");
  }
  const withProject = expand(
    studio.urls.scene_detail_template,
    "__project_id__",
    projectId,
  );
  const url = expand(withProject, "__scene_id__", encodeURIComponent(sceneId));
  await request<never>(url, { method: "DELETE" });
}

export async function setCharacterReference(
  studio: StudioBootstrap,
  characterId: string,
  assetId: string,
): Promise<Character> {
  const url = expand(
    studio.urls.character_reference_template,
    "__character_id__",
    characterId,
  );
  const body = await request<Character>(url, {
    method: "POST",
    body: JSON.stringify({ asset_id: assetId }),
  });
  const character = (body as ApiEnvelope<Character> & { character?: Character }).character;
  if (!character) {
    throw new ComicApiError("参考画像の設定結果にキャラクターデータがありません。");
  }
  return character;
}

export async function generateCharacterSheet(
  studio: StudioBootstrap,
  characterId: string,
  sourceAssetIds: string[],
): Promise<string> {
  const url = expand(
    studio.urls.character_generate_template,
    "__character_id__",
    characterId,
  );
  return submitJob(url, {
      source_asset_ids: sourceAssetIds,
  });
}

export async function updateProjectData(
  studio: StudioBootstrap,
  projectData: ProjectData,
  title?: string,
): Promise<void> {
  const projectId = studio.active_project?.id;
  if (!projectId) {
    throw new ComicApiError("更新できる漫画プロジェクトがありません。");
  }
  const url = expand(studio.urls.project_detail_template, "__project_id__", projectId);
  await request<never>(url, {
    method: "PATCH",
    body: JSON.stringify({
      ...(title === undefined ? {} : { title }),
      project_data: projectData,
    }),
  });
}

export async function changePageLayout(
  studio: StudioBootstrap,
  pageId: string,
  layoutKey: string,
): Promise<ComicPage> {
  const url = expand(studio.urls.page_detail_template, "__page_id__", pageId);
  await request<never>(url, {
    method: "PATCH",
    body: JSON.stringify({ layout_key: layoutKey }),
  });
  return loadComicPage(studio, pageId);
}

export async function generatePanel(
  studio: StudioBootstrap,
  panelId: string,
  operation: string,
  requestedChange: string,
  _outputCount: number,
  sourceAssetId: string | null = null,
  analysisJobId: string | null = null,
  expectedCredits?: number,
): Promise<string> {
  const url = expand(studio.urls.panel_generate_template, "__panel_id__", panelId);
  return submitJob(url, {
      operation,
      output_count: 1,
      expected_credits: expectedCredits ?? (operation === "generate" ? undefined : studio.operation_credits[operation]),
      requested_change: requestedChange,
      ...(sourceAssetId ? { source_asset_id: sourceAssetId } : {}),
      ...(analysisJobId ? { analysis_job_id: analysisJobId } : {}),
  });
}

export async function analyzePanel(
  studio: StudioBootstrap,
  panelId: string,
  assetId: string,
): Promise<string> {
  const url = expand(studio.urls.panel_analysis_template, "__panel_id__", panelId);
  return submitJob(url, {
      asset_id: assetId,
      client_request_id: crypto.randomUUID(),
  });
}

export async function createStoryboard(
  studio: StudioBootstrap,
  input: {
    storyText: string;
    polishDialogue?: boolean;
    sourceType: "story" | "chapter_summary" | "scene" | "dialogue" | "script" | "storyboard";
    expectedPages: number;
    colorMode: ProjectData["color_mode"];
    readingDirection: ProjectData["reading_direction"];
    preferredLayoutKey: string;
  },
): Promise<string> {
  const projectId = studio.active_project?.id;
  if (!projectId) {
    throw new ComicApiError("ネームを生成できる漫画プロジェクトがありません。");
  }
  const url = expand(studio.urls.storyboard_create_template, "__project_id__", projectId);
  return submitJob(url, {
      story_text: input.storyText,
      polish_dialogue: input.polishDialogue ?? false,
      source_type: input.sourceType,
      expected_pages: input.expectedPages,
      color_mode: input.colorMode,
      reading_direction: input.readingDirection,
      preferred_layout_key: input.preferredLayoutKey,
      client_request_id: crypto.randomUUID(),
  });
}

function retryUrlFromDetailUrl(detailUrl: string): string {
  const markerIndex = detailUrl.search(/[?#]/);
  const path = markerIndex === -1 ? detailUrl : detailUrl.slice(0, markerIndex);
  const suffix = markerIndex === -1 ? "" : detailUrl.slice(markerIndex);
  return `${path.replace(/\/+$/, "")}/retry/${suffix}`;
}

export async function retryJob(studio: StudioBootstrap, jobId: string): Promise<string> {
  const detailUrl = expand(studio.urls.job_detail_template, "__job_id__", jobId);
  const body = await request<never>(retryUrlFromDetailUrl(detailUrl), {
    method: "POST",
    body: "{}",
  });
  if (!body.job_id) {
    throw new ComicApiError("再開したタスクの受付結果を確認できませんでした。");
  }
  return body.job_id;
}

export async function selectPanelAsset(
  studio: StudioBootstrap,
  panelId: string,
  assetId: string,
): Promise<Panel> {
  const url = expand(studio.urls.panel_select_asset_template, "__panel_id__", panelId);
  const body = await request<Panel>(url, {
    method: "POST",
    body: JSON.stringify({ asset_id: assetId }),
  });
  if (!body.panel) {
    throw new ComicApiError("候補画像の選択結果にコマデータがありません。");
  }
  return body.panel;
}

export async function exportPage(studio: StudioBootstrap, pageId: string, expectedRevision?: number): Promise<string> {
  const url = expand(studio.urls.page_export_template, "__page_id__", pageId);
  return submitJob(url, {
      format: "png",
      scale: 1,
      expected_revision: expectedRevision,
  });
}

export async function exportProject(
  studio: StudioBootstrap,
  format: "zip" | "pdf",
  scale: 1 | 2,
  expectedRevisions: Record<string, number> = {},
): Promise<string> {
  const projectId = studio.active_project?.id;
  if (!projectId) {
    throw new ComicApiError("書き出せる漫画プロジェクトがありません。");
  }
  const url = expand(studio.urls.project_export_template, "__project_id__", projectId);
  return submitJob(url, {
      format,
      scale,
      expected_revisions: expectedRevisions,
  });
}

function delay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ComicApiError("タスクの追跡を終了しました。", "job_poll_aborted"));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, milliseconds);
    const abort = () => {
      window.clearTimeout(timer);
      reject(new ComicApiError("タスクの追跡を終了しました。", "job_poll_aborted"));
    };
    signal?.addEventListener("abort", abort, { once: true });
  });
}

export interface WaitForJobOptions {
  maxWaitMs?: number;
  onUpdate?: (job: ComicJob) => void;
  signal?: AbortSignal;
}

export async function waitForJob(
  studio: StudioBootstrap,
  jobId: string,
  options: WaitForJobOptions = {},
): Promise<ComicJob> {
  const url = expand(studio.urls.job_detail_template, "__job_id__", jobId);
  const startedAt = Date.now();
  const maxWaitMs = options.maxWaitMs ?? 65 * 60 * 1000;
  let connectionFailures = 0;
  while (Date.now() - startedAt < maxWaitMs) {
    if (options.signal?.aborted) {
      throw new ComicApiError("タスクの追跡を終了しました。", "job_poll_aborted");
    }
    try {
      const body = await request<ComicJob>(url, { signal: options.signal });
      if (!body.job) {
        throw new ComicApiError("タスクの取得結果に状態データがありません。");
      }
      connectionFailures = 0;
      options.onUpdate?.(body.job);
      if (body.job.status === "success") {
        return body.job;
      }
      if (body.job.status === "failed") {
        throw new ComicApiError(
          body.job.error_message || "漫画タスクに失敗しました。",
          "job_failed",
        );
      }
    } catch (error) {
      const apiError = error instanceof ComicApiError ? error : null;
      if (
        options.signal?.aborted
        || apiError?.code === "job_poll_aborted"
        || apiError?.code === "job_failed"
        || (apiError !== null
          && !["request_failed", "service_unavailable"].includes(apiError.code))
      ) {
        throw error;
      }
      connectionFailures += 1;
    }
    const elapsedMs = Date.now() - startedAt;
    const intervalMs = typeof document !== "undefined" && document.hidden
      ? 15_000
      : connectionFailures > 0
        ? Math.min(15_000, 3000 * connectionFailures)
        : elapsedMs < 60_000
          ? 3000
          : 10_000;
    if (elapsedMs + intervalMs >= maxWaitMs) {
      break;
    }
    await delay(intervalMs, options.signal);
  }
  throw new ComicApiError(
    "画面での追跡を一時停止しました。生成処理は継続中です。タスク一覧から確認してください。",
    "job_poll_timeout",
  );
}

export async function applyStoryboard(
  studio: StudioBootstrap,
  jobId: string,
  storyboard: StoryboardResult,
): Promise<void> {
  const projectId = studio.active_project?.id;
  if (!projectId) {
    throw new ComicApiError("ネームを反映できる漫画プロジェクトがありません。");
  }
  const url = studio.urls.storyboard_apply_template
    .replace("__project_id__", encodeURIComponent(projectId))
    .replace("__job_id__", encodeURIComponent(jobId));
  await request<never>(url, {
    method: "POST",
    body: JSON.stringify({ storyboard }),
  });
}


export interface GenerationPreview {
  credits: number; usable_credits: number;
  sources: Array<{id:string; download_url:string; kinds:string[]}>;
  previous_panel_id: string | null;
  previous_options: Array<{id:string; label:string}>;
}

export async function previewGeneration(studio: StudioBootstrap, panelId: string, operation: string, sourceAssetId: string | null, content?: Panel["content"]): Promise<GenerationPreview> {
  const url = expand(studio.urls.panel_generate_template, "__panel_id__", panelId) + "?preview=1";
  const body = await request<GenerationPreview>(url, { method:"POST", body:JSON.stringify({operation, output_count:1, client_request_id:crypto.randomUUID(), ...(sourceAssetId ? {source_asset_id:sourceAssetId}:{}), ...(content ? {content}:{})}) });
  if (!body.data) throw new ComicApiError("生成前の費用と参考画像を確認できませんでした。");
  return body.data;
}

export async function refreshAsset(
  studio: StudioBootstrap,
  assetId: string,
  purpose?: "view" | "download",
): Promise<Asset> {
  const detailUrl = expand(studio.urls.asset_detail_template, "__asset_id__", assetId);
  const url = purpose
    ? `${detailUrl}${detailUrl.includes("?") ? "&" : "?"}purpose=${encodeURIComponent(purpose)}`
    : detailUrl;
  const body = await request<Asset>(url);
  if (!body.asset) throw new ComicApiError("素材が見つかりません。");
  return body.asset;
}

export async function downloadAsset(
  studio: StudioBootstrap,
  assetId: string,
  filename?: string,
): Promise<void> {
  const asset = await refreshAsset(studio, assetId, "download");
  const response = await fetch(asset.download_url);
  if (!response.ok) throw new ComicApiError("ファイルをダウンロードできませんでした。もう一度お試しください。");
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || asset.filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function uploadPanelRevision(studio: StudioBootstrap, panelId: string, file: File): Promise<Asset> {
  if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 20 * 1024 * 1024) throw new ComicApiError("20MB以内のPNG・JPG・WebPを選んでください。");
  try {
    const target = {project_id:studio.active_project?.id, panel_id:panelId, asset_type:"upload", content_type:file.type};
    const signed = await request<{upload_url:string; storage_key:string}>(studio.urls.asset_sign, {method:"POST", body:JSON.stringify({...target, filename:file.name, file_size:file.size})}) as ApiEnvelope<never> & {upload_url:string; storage_key:string};
    const uploaded = await fetch(signed.upload_url, {method:"PUT", headers:{"Content-Type":file.type}, body:file});
    if (!uploaded.ok) throw new ComicApiError("修正版をアップロードできませんでした。現在の画像は変更されていません。");
    const result = await request<Asset>(studio.urls.asset_register, {method:"POST", body:JSON.stringify({...target, storage_key:signed.storage_key, width:0, height:0})});
    if (!result.asset) throw new ComicApiError("修正版を登録できませんでした。");
    return result.asset;
  } catch (error) {
    if (error instanceof ComicApiError) throw error;
    throw new ComicApiError("修正版をアップロードできませんでした。ネットワーク接続を確認して、もう一度お試しください。現在の画像は変更されていません。");
  }
}
